import {
  reactive, provide, onBeforeUnmount,
} from 'vue';
import axios from 'axios';

import store from '@/stores';

export const LISTING_SEARCH = Symbol('listingSearch');

const CANCEL_OFFER_DELAY = 2000;
const SUPER_ROLE_ID = 6;
const CONTROL_SELECTOR = 'button, a, [role="button"]';

const canCancelSearch = () => {
  const roles = store.state.user.userInfo?.roles_ids || [];
  return roles.includes(SUPER_ROLE_ID) && store.getters['user/isInternalUser'];
};

const hasKeptFilters = () => Object.keys(store.getters['proposal/validFilters']).length > 0;

export const isEnterFromControl = (event) => Boolean(event?.target?.closest?.(CONTROL_SELECTOR));

export function createListingSearch() {
  let controller = null;
  let offerTimer = null;
  let disposed = false;

  const listing = reactive({
    awaitingSearch: false,
    cancelled: false,
    cancelOffered: false,
  });

  const withdrawOffer = () => {
    clearTimeout(offerTimer);
    listing.cancelOffered = false;
  };

  listing.arrive = (search) => {
    if (disposed) return undefined;
    if (!hasKeptFilters()) return search();
    listing.awaitingSearch = true;
    return undefined;
  };

  listing.start = () => {
    withdrawOffer();
    listing.awaitingSearch = false;
    listing.cancelled = false;
    controller?.abort();
    controller = new AbortController();

    if (canCancelSearch()) {
      offerTimer = setTimeout(() => { listing.cancelOffered = true; }, CANCEL_OFFER_DELAY);
    }

    return controller.signal;
  };

  listing.finish = (signal) => {
    if (controller?.signal !== signal) return false;
    withdrawOffer();
    controller = null;
    return true;
  };

  listing.cancel = () => {
    if (!controller || controller.signal.aborted) return;
    controller.abort();
    withdrawOffer();
    listing.cancelled = true;
  };

  listing.isCancel = (err) => axios.isCancel(err);

  listing.dispose = () => {
    disposed = true;
    withdrawOffer();
    if (!controller) return;
    controller.abort();
    controller = null;
    store.dispatch('app/SET_LOADING', false);
  };

  return listing;
}

export default function useListingSearch() {
  const listing = createListingSearch();

  provide(LISTING_SEARCH, listing);
  onBeforeUnmount(listing.dispose);

  return listing;
}
